import React, { useState } from 'react';
import TopNavigation from 'components/ui/TopNavigation';
import QuickActionToolbar from 'components/ui/QuickActionToolbar';
import NotificationAlertBar from 'components/ui/NotificationAlertBar';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import InsightCard from './components/InsightCard';
import ForecastPanel from './components/ForecastPanel';
import TerritoryRecommendations from './components/TerritoryRecommendations';
import ReportExporter from './components/ReportExporter';
import SeasonalDemandChart from './components/SeasonalDemandChart';
import NLPSummaryPanel from './components/NLPSummaryPanel';
import AlertsPanel from './components/AlertsPanel';

const insightsData = [
  {
    id: 1,
    type: 'opportunity',
    title: 'Cebu Market Expansion Opportunity',
    category: 'Territory Growth',
    date: 'Mar 06, 2026',
    confidence: 'high',
    summary: 'AI analysis of demographic and retail density data identifies 47 unserved outlets in Cebu City Zone 3. Strong demand for Shampoo and Body Wash SKUs with competitor coverage gaps and rising disposable income signal strong expansion potential.',
    metrics: [
      { label: 'Potential Revenue', value: '₱380K/mo' },
      { label: 'Unserved Outlets', value: '47' },
      { label: 'Coverage Gap', value: '34%' },
    ],
    territory: 'Visayas - Cebu',
  },
  {
    id: 2,
    type: 'warning',
    title: 'NCR Account Churn Risk Detected',
    category: 'Account Retention',
    date: 'Mar 05, 2026',
    confidence: 'high',
    summary: 'Machine learning models detect behavioral patterns in 23 NCR accounts consistent with pre-churn signals: declining order frequency for Toothpaste and Mouthwash SKUs, reduced basket size, and delayed payments over 90 days.',
    metrics: [
      { label: 'At-Risk Revenue', value: '₱1.2M' },
      { label: 'Accounts', value: '23' },
      { label: 'Avg Decline', value: '-38%' },
    ],
    territory: 'NCR',
  },
  {
    id: 3,
    type: 'trend',
    title: 'Personal Care Surge — Summer Effect',
    category: 'Product Trend',
    date: 'Mar 04, 2026',
    confidence: 'high',
    summary: 'Body Wash and Deodorant SKUs showing 24% month-over-month growth driven by early summer heat. Historical patterns suggest peak demand in April-May. Inventory pre-positioning recommended across all regions.',
    metrics: [
      { label: 'MoM Growth', value: '+24%' },
      { label: 'Top SKU', value: 'BW-001' },
      { label: 'Peak Month', value: 'May' },
    ],
    territory: 'All Territories',
  },
  {
    id: 4,
    type: 'anomaly',
    title: 'Mindanao Sales Anomaly Resolved',
    category: 'Performance Anomaly',
    date: 'Mar 03, 2026',
    confidence: 'medium',
    summary: 'After 2 months of declining performance, Mindanao territory shows recovery with 18% week-over-week improvement. New area manager onboarding and revised route plans appear to be driving the turnaround in Soap and Lotion categories.',
    metrics: [
      { label: 'WoW Recovery', value: '+18%' },
      { label: 'New Accounts', value: '+31' },
      { label: 'Coverage', value: '78%' },
    ],
    territory: 'Mindanao',
  },
  {
    id: 5,
    type: 'forecast',
    title: 'Q2 Revenue Forecast: ₱155.5M',
    category: 'Sales Forecast',
    date: 'Mar 02, 2026',
    confidence: 'high',
    summary: 'AI forecasting models project Q2 2026 total revenue of ₱155.5M, representing 15.8% YoY growth. Fiesta season and school opening drive demand for Shampoo, Conditioner, and Facial Wash. Visayas and Mindanao show highest growth potential.',
    metrics: [
      { label: 'Q2 Forecast', value: '₱155.5M' },
      { label: 'YoY Growth', value: '+15.8%' },
      { label: 'Accuracy', value: '87.6%' },
    ],
    territory: 'National',
  },
  {
    id: 6,
    type: 'opportunity',
    title: 'Luzon Rural Market Penetration',
    category: 'Market Expansion',
    date: 'Mar 01, 2026',
    confidence: 'medium',
    summary: 'Infrastructure improvements in Luzon rural areas have increased accessibility for distribution. 89 new viable retail accounts identified across 12 municipalities with combined potential of ₱620,000 monthly for Dishwashing Liquid and Soap (Bar).',
    metrics: [
      { label: 'New Accounts', value: '89' },
      { label: 'Potential', value: '₱620K/mo' },
      { label: 'Municipalities', value: '12' },
    ],
    territory: 'Luzon',
  },
];

const TABS = [
  { id: 'insights', label: 'AI Insights', icon: 'BrainCircuit' },
  { id: 'forecast', label: 'Forecasting', icon: 'TrendingUp' },
  { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
  { id: 'reports', label: 'Reports', icon: 'Download' },
  { id: 'alerts', label: 'Alerts', icon: 'Bell' },
];

const FILTER_OPTIONS = ['All', 'opportunity', 'warning', 'trend', 'anomaly', 'forecast'];

const KPI_STATS = [
  { label: 'Insights Generated', value: '142', icon: 'BrainCircuit', color: '#DC2626', bgStyle: { background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.2)' } },
  { label: 'Avg Confidence', value: '89.2%', icon: 'Target', color: '#22C55E', bgStyle: { background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' } },
  { label: 'Active Alerts', value: '4', icon: 'Bell', color: '#EF4444', bgStyle: { background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' } },
  { label: 'Reports Ready', value: '12', icon: 'FileText', color: '#D4AF37', bgStyle: { background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)' } },
];

export default function AIIntelligenceReports() {
  const [activeTab, setActiveTab] = useState('insights');
  const [insightFilter, setInsightFilter] = useState('All');
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const filteredInsights = insightsData?.filter(
    (ins) => insightFilter === 'All' || ins?.type === insightFilter
  );

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1800);
  };

  const handleViewDetails = (insight) => {
    setSelectedInsight(insight);
  };

  const handleAction = (action) => {
    if (action === 'generate') handleRefresh();
  };

  return (
    <div className="min-h-screen bg-background page-gradient-bg">
      <TopNavigation />
      <NotificationAlertBar />
      <QuickActionToolbar onAction={handleAction} />
      <main className="px-4 md:px-6 lg:px-8 py-3 md:py-4 lg:py-6 pb-20 lg:pb-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 md:mb-6">
          <div className="flex items-center gap-2 md:gap-3">
            <div
              className="flex items-center justify-center rounded-xl flex-shrink-0"
              style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #DC2626, #B91C1C)', boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)' }}
            >
              <Icon name="BrainCircuit" size={20} color="#FFFFFF" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl text-foreground">
                AI Intelligence Reports
              </h1>
              <p className="font-caption text-xs md:text-sm text-muted-foreground mt-0.5">
                Machine learning insights · Updated {new Date()?.toLocaleString('en-PH', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              iconName={refreshing ? 'Loader' : 'RefreshCw'}
              iconPosition="left"
              iconSize={15}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh AI'}
            </Button>
            <Button variant="default" size="sm" iconName="Download" iconPosition="left" iconSize={15}>
              Export All
            </Button>
          </div>
        </div>

        {/* KPI Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4 mb-4 md:mb-6">
          {KPI_STATS?.map((stat) => (
            <div key={stat?.label} className="flex items-center gap-2 md:gap-3 p-2.5 md:p-3 lg:p-4 rounded-xl" style={stat?.bgStyle}>
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ width: 36, height: 36, background: `${stat?.color}20` }}
              >
                <Icon name={stat?.icon} size={18} color={stat?.color} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="font-data text-lg md:text-xl font-bold text-foreground leading-tight">{stat?.value}</p>
                <p className="font-caption text-xs text-muted-foreground leading-tight">{stat?.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-6 border-b border-border">
          {TABS?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-t-lg font-caption text-sm font-medium transition-all duration-200 flex-shrink-0 border-b-2 -mb-px ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              style={activeTab === tab?.id ? { background: 'rgba(220, 38, 38, 0.08)' } : {}}
            >
              <Icon name={tab?.icon} size={15} color="currentColor" strokeWidth={2} />
              <span>{tab?.label}</span>
              {tab?.id === 'alerts' && (
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white font-data text-xs leading-none">4</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'insights' && (
          <div>
            {/* Filter Row */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span className="font-caption text-xs text-muted-foreground">Filter:</span>
              {FILTER_OPTIONS?.map((f) => (
                <button
                  key={f}
                  onClick={() => setInsightFilter(f)}
                  className={`px-3 py-1 rounded-lg border font-caption text-xs font-medium transition-all ${
                    insightFilter === f
                      ? 'text-primary' : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                  style={insightFilter === f ? { borderColor: 'rgba(220, 38, 38, 0.4)', background: 'rgba(220, 38, 38, 0.08)' } : {}}
                >
                  {f?.charAt(0)?.toUpperCase() + f?.slice(1)}
                </button>
              ))}
              <span className="ml-auto font-caption text-xs text-muted-foreground">{filteredInsights?.length} insights</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredInsights?.map((insight) => (
                <InsightCard key={insight?.id} insight={insight} onViewDetails={handleViewDetails} />
              ))}
            </div>

            {filteredInsights?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="SearchX" size={40} color="var(--color-muted-foreground)" strokeWidth={1.5} />
                <p className="font-heading font-semibold text-foreground mt-3">No insights found</p>
                <p className="font-caption text-sm text-muted-foreground mt-1">Try a different filter</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'forecast' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
            <ForecastPanel />
            <SeasonalDemandChart />
            <div className="xl:col-span-2">
              <NLPSummaryPanel />
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <TerritoryRecommendations />
        )}

        {activeTab === 'reports' && (
          <div className="max-w-2xl">
            <ReportExporter />
          </div>
        )}

        {activeTab === 'alerts' && (
          <AlertsPanel />
        )}
      </main>
      {/* Insight Detail Modal */}
      {selectedInsight && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(17,24,39,0.5)' }}
          onClick={() => setSelectedInsight(null)}
        >
          <div
            className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-xl"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
              <div>
                <h3 className="font-heading font-bold text-base text-foreground">{selectedInsight?.title}</h3>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">{selectedInsight?.category} · {selectedInsight?.date}</p>
              </div>
              <button
                onClick={() => setSelectedInsight(null)}
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
              >
                <Icon name="X" size={18} color="var(--color-muted-foreground)" strokeWidth={2} />
              </button>
            </div>
            <div className="p-5">
              <p className="font-body text-sm text-foreground leading-relaxed mb-4">{selectedInsight?.summary}</p>
              <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-muted rounded-xl">
                {selectedInsight?.metrics?.map((m, i) => (
                  <div key={i} className="text-center">
                    <p className="font-data text-base font-bold text-foreground">{m?.value}</p>
                    <p className="font-caption text-xs text-muted-foreground">{m?.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="font-caption text-sm text-muted-foreground">{selectedInsight?.territory}</span>
                <Button variant="default" size="sm" className="ml-auto" iconName="ExternalLink" iconPosition="right" iconSize={14}>
                  Full Analysis
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}